import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import RoyalAssistant from '@/components/RoyalAssistant';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import CitizenDashboard from './pages/CitizenDashboard';
import InstitutionDashboard from './pages/InstitutionDashboard';
import GovernmentDashboard from './pages/GovernmentDashboard';
import InternationalDashboard from './pages/InternationalDashboard';
import PublicPortal from './pages/PublicPortal';
import BondMarketplace from './pages/BondMarketplace';
import AIMonitoring from './pages/AIMonitoring';
import WalletPage from './pages/WalletPage';
import SovereignWealthFund from './pages/SovereignWealthFund';
import KALMarket from './pages/KALMarket';
import KALTransparency from './pages/KALTransparency';
import PaymentsPage from './pages/PaymentsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } else {
      // Try cookie-based auth
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch {
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    await checkAuth();
    return response.data;
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    await checkAuth();
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, token, login, register, logout, loginWithGoogle, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground font-mono text-sm uppercase tracking-widest">Verifying Credentials</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const roleRoutes = {
      citizen: '/citizen',
      institution: '/institution',
      government: '/government',
      international: '/international',
      admin: '/government'
    };
    return <Navigate to={roleRoutes[user.role] || '/citizen'} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/public" element={<PublicPortal />} />
      <Route path="/bonds" element={<BondMarketplace />} />
      <Route path="/bonds/success" element={<BondMarketplace />} />
      <Route path="/wealth-fund" element={<SovereignWealthFund />} />
      <Route path="/tnb/market" element={<KALMarket />} />
      <Route path="/tnb/transparency" element={<KALTransparency />} />
      <Route path="/payments" element={<PaymentsPage />} />

      {/* Protected Routes - Citizen */}
      <Route path="/citizen" element={
        <ProtectedRoute allowedRoles={['citizen', 'admin']}>
          <CitizenDashboard />
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute allowedRoles={['citizen', 'admin']}>
          <WalletPage />
        </ProtectedRoute>
      } />

      {/* Protected Routes - Institution */}
      <Route path="/institution" element={
        <ProtectedRoute allowedRoles={['institution', 'admin']}>
          <InstitutionDashboard />
        </ProtectedRoute>
      } />

      {/* Protected Routes - Government */}
      <Route path="/government" element={
        <ProtectedRoute allowedRoles={['government', 'admin']}>
          <GovernmentDashboard />
        </ProtectedRoute>
      } />

      {/* Protected Routes - International */}
      <Route path="/international" element={
        <ProtectedRoute allowedRoles={['international', 'admin']}>
          <InternationalDashboard />
        </ProtectedRoute>
      } />

      {/* AI Monitoring - Restricted */}
      <Route path="/ai-monitoring" element={
        <ProtectedRoute allowedRoles={['government', 'admin']}>
          <AIMonitoring />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-background">
          <AppRouter />
          <RoyalAssistant />
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
